import { ReactNode, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAsyncEntity } from '@backstage/plugin-catalog-react';
import { makeStyles } from 'tss-react/mui';
import Button from '@mui/material/Button';
import yaml from 'js-yaml';
import type { TabNavItem } from '@mercedes-benz/odp-theme-v2';
import {
  ClipboardIcon,
  getColors,
  PageBreadcrumbs,
  TabNav,
  typography,
} from '@mercedes-benz/odp-theme-v2';
import { TabErrorBoundary } from './TabErrorBoundary';

const KIND_LABELS: Record<string, string> = {
  component: 'Component Details',
  api: 'API Details',
  system: 'System Details',
  domain: 'Domain Details',
  group: 'Group Details',
  user: 'User Details',
  resource: 'Resource Details',
  content: 'Content Details',
  seamless: 'Seamless Details',
  systemrole: 'System Role Details',
  academy: 'Academy Details',
  repository: 'Repository Details',
  infrastructure: 'Infrastructure Details',
};

interface EntityPageLayoutProps {
  tabs: TabNavItem[];
  children: (activeTab: string) => ReactNode;
}

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    root: {
      backgroundColor: c.background.page,
      minHeight: '100vh',
      overflowY: 'auto',
    },
    content: {
      padding: 24,
      maxWidth: 1500,
      margin: '0 auto',
    },
    titleRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 24,
    },
    title: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '1.5rem',
      fontWeight: 600,
      color: c.text.primary,
      margin: 0,
      userSelect: 'none',
    },
    subtitle: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.875rem',
      fontWeight: 400,
      color: c.text.secondary,
      margin: 0,
      marginTop: 4,
      userSelect: 'none',
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    iconButton: {
      width: 36,
      height: 36,
      minWidth: 36,
      padding: 0,
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: fgAlpha(0.05),
      color: fgAlpha(0.55),
      transition: 'background 0.15s ease, color 0.15s ease',
      '& svg': { width: 18, height: 18 },
      '&:hover': {
        background: fgAlpha(0.1),
        color: fgAlpha(0.8),
      },
    },
    tabContent: {
      marginTop: 24,
    },
    /* YAML overlay */
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: isLight ? 'rgba(0, 0, 0, 0.35)' : 'rgba(0, 0, 0, 0.6)',
      zIndex: 1300,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    yamlPanel: {
      width: '90%',
      maxWidth: 720,
      maxHeight: '80vh',
      background: c.background.paper,
      border: `1px solid ${c.border.default}`,
      borderRadius: 12,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxShadow: isLight
        ? '0 8px 32px rgba(0,0,0,0.12)'
        : '0 8px 32px rgba(0,0,0,0.5)',
    },
    yamlHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px',
      borderBottom: `1px solid ${c.border.default}`,
    },
    yamlTitle: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: 14,
      fontWeight: 600,
      color: c.text.primary,
      margin: 0,
      userSelect: 'none',
    },
    yamlActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    yamlCopyBtn: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: 12,
      fontWeight: 500,
      padding: '4px 12px',
      borderRadius: 6,
      border: `1px solid ${c.border.default}`,
      background: 'transparent',
      color: c.text.secondary,
      cursor: 'pointer',
      transition: 'background 0.15s ease, color 0.15s ease',
      '&:hover': {
        background: fgAlpha(0.06),
        color: c.text.primary,
      },
    },
    yamlCloseBtn: {
      width: 28,
      height: 28,
      border: 'none',
      borderRadius: '50%',
      background: 'transparent',
      color: c.text.secondary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 18,
      lineHeight: 1,
      transition: 'background 0.15s ease, color 0.15s ease',
      '&:hover': {
        background: fgAlpha(0.08),
        color: c.text.primary,
      },
    },
    yamlBody: {
      flex: 1,
      overflowY: 'auto',
      padding: 16,
    },
    yamlCode: {
      fontFamily: 'monospace',
      fontSize: 13,
      lineHeight: 1.6,
      color: c.text.primary,
      whiteSpace: 'pre',
      margin: 0,
      tabSize: 2,
    },
  };
});

export const EntityPageLayout = ({
  tabs,
  children,
}: Readonly<EntityPageLayoutProps>) => {
  const { classes } = useStyles();
  const { entity } = useAsyncEntity();
  const {
    namespace,
    kind,
    name,
    '*': tabSlug,
  } = useParams<{
    namespace: string;
    kind: string;
    name: string;
    '*': string;
  }>();
  const navigate = useNavigate();
  const [yamlOpen, setYamlOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const defaultTab = tabs[0]?.id ?? 'overview';
  const activeTab = tabs.find(t => t.id === tabSlug)?.id ?? defaultTab;
  const basePath = `/catalog/${namespace ?? 'default'}/${kind ?? 'component'}/${name ?? ''}`;

  const handleTabChange = useCallback(
    (tabId: string) => {
      if (tabId === defaultTab) {
        navigate(basePath, { replace: true });
      } else {
        navigate(`${basePath}/${tabId}`, { replace: true });
      }
    },
    [navigate, basePath, defaultTab],
  );

  const entityYaml = entity
    ? yaml.dump(entity, { lineWidth: -1, noRefs: true })
    : '';

  const handleCopyYaml = useCallback(() => {
    globalThis.navigator.clipboard.writeText(entityYaml).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [entityYaml]);

  if (!entity) return null;

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <PageBreadcrumbs
          segments={[
            { label: 'Catalog', to: '/catalog' },
            { label: entity.metadata.name },
          ]}
        />
        <div className={classes.titleRow}>
          <div>
            <h1 className={classes.title}>{entity.metadata.name}</h1>
            <p className={classes.subtitle}>
              {KIND_LABELS[entity.kind.toLowerCase()] ??
                `${entity.kind} Details`}
            </p>
          </div>
          <div className={classes.actions}>
            <Button
              type="button"
              className={classes.iconButton}
              aria-label="View raw entity YAML"
              onClick={() => setYamlOpen(true)}
            >
              <ClipboardIcon />
            </Button>
          </div>
        </div>

        <TabNav tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />

        <div className={classes.tabContent}>
          <TabErrorBoundary key={activeTab}>
            {children(activeTab)}
          </TabErrorBoundary>
        </div>
      </div>

      {yamlOpen && (
        <div
          className={classes.overlay}
          onClick={() => setYamlOpen(false)}
          role="presentation"
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div
            className={classes.yamlPanel}
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-label="Raw entity YAML"
          >
            <div className={classes.yamlHeader}>
              <h3 className={classes.yamlTitle}>
                {entity.metadata.name} &mdash; Raw Entity
              </h3>
              <div className={classes.yamlActions}>
                <Button
                  type="button"
                  className={classes.yamlCopyBtn}
                  onClick={handleCopyYaml}
                >
                  {copied ? 'Copied!' : 'Copy YAML'}
                </Button>
                <Button
                  type="button"
                  className={classes.yamlCloseBtn}
                  onClick={() => setYamlOpen(false)}
                  aria-label="Close"
                >
                  &#x2715;
                </Button>
              </div>
            </div>
            <div className={classes.yamlBody}>
              <pre className={classes.yamlCode}>{entityYaml}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
